import styled from '@emotion/styled';
import { ModulesStates } from '@racemap/utilities/consts/events';
import type { SponsorLogo } from '@racemap/utilities/types/events';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useElementDimensions } from 'src/lib/customHooks';
import {
  UNMEASURED,
  arrangeByAlignment,
  fitPrefix,
  renderedWidth,
  sizesPending,
  useNaturalSizes,
  useRotatingWindow,
} from 'src/lib/sponsorLogos';
import { useSelectedEventData } from 'src/store/leaderboard/leaderboard_reducer';
import { useLeaderboardEnvironmentInfo } from './utils';

const GAP_SIZE = 12;
const FADE_DURATION = 300; // ms

export const SponsorLogos: FC = () => {
  const selectedEventData = useSelectedEventData();
  const event = selectedEventData?.eventObject;
  const sponsorModule = event?.modules.sponsor;
  const { isMobile } = useLeaderboardEnvironmentInfo();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementDimensions(containerRef);
  const [isVisible, setIsVisible] = useState(true);
  const [displayedLogos, setDisplayedLogos] = useState<Array<SponsorLogo>>([]);

  const logos = sponsorModule?.logos;
  const logoHeight = isMobile ? 55 : 75;
  const naturalSizes = useNaturalSizes(logos);

  const isActive =
    sponsorModule != null &&
    sponsorModule.state === ModulesStates.ACTIVATED &&
    logos != null &&
    logos.length > 0;

  const pending = sizesPending(logos, naturalSizes);

  // The logo images are height-locked by CSS, so only the height constrains them
  const widthOf = useCallback(
    (logo: SponsorLogo) =>
      renderedWidth(naturalSizes[logo.id] ?? UNMEASURED, {
        maxWidth: Number.POSITIVE_INFINITY,
        maxHeight: logoHeight,
      }),
    [naturalSizes, logoHeight],
  );

  const fit = useCallback(
    (order: ReadonlyArray<SponsorLogo>) =>
      pending ? [] : fitPrefix(order, widthOf, GAP_SIZE, containerWidth),
    [pending, widthOf, containerWidth],
  );

  const selectedLogos = useRotatingWindow(logos, fit);

  // Pinned first, then widest, at the anchor the alignment sets
  const alignment = sponsorModule?.alignment ?? 'left';
  const arrangedLogos = useMemo(
    () => arrangeByAlignment(selectedLogos, widthOf, alignment),
    [selectedLogos, widthOf, alignment],
  );

  // Handle logo changes with fade transition
  useEffect(() => {
    // If we already have logos displayed, fade out first
    if (displayedLogos.length > 0 && arrangedLogos.length > 0) {
      setIsVisible(false);
      const timeoutId = setTimeout(() => {
        setDisplayedLogos(arrangedLogos);
        setIsVisible(true);
      }, FADE_DURATION);
      return () => clearTimeout(timeoutId);
    }

    // First time showing logos - just show them
    setDisplayedLogos(arrangedLogos);
    setIsVisible(true);
  }, [arrangedLogos]);

  // Always render the container so the ref is attached and width can be measured
  // Use height 0 when not active to avoid taking up space
  return (
    <Container
      ref={containerRef}
      className="sponsor-logos-container"
      style={{ justifyContent: sponsorModule?.alignment || 'left' }}
      height={isActive ? (isMobile ? '60px' : '80px') : '0px'}
      isVisible={isVisible}
    >
      {displayedLogos.map((logo) => (
        <Logo
          href={logo.url}
          title={logo.title}
          key={logo.id}
          target="_blank"
          rel="noopener noreferrer"
          height={isMobile ? '55px' : '75px'}
        >
          <img src={logo.imageUrls?.logo} alt={logo.altText} />
        </Logo>
      ))}
    </Container>
  );
};

const Logo = styled.a<{ height: string }>`
  cursor: pointer;

  img {
    height: ${(props) => props.height};
  }
`;

const Container = styled.div<{ height: string; isVisible: boolean }>`
  display: flex;
  height: ${(props) => props.height};
  gap: 12px;
  width: 100%;
  overflow: hidden;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity ${FADE_DURATION}ms ease-in-out;
`;
